import React, { useState } from 'react';
import {
  Printer, CheckCircle2, ArrowRight, Briefcase,
  Megaphone, Maximize2, BookOpen, PhoneCall, X, User, Phone, Mail, Send, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const PrintingSolution = () => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    quantity: '500 Pcs',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const printCategories = [
    {
      title: 'Business Essentials',
      icon: Briefcase,
      badgeColor: 'from-orange-500 to-amber-500',
      items: [
        { name: 'Visiting Cards', svg: '/images/printing/visiting-cards.svg' },
        { name: 'Letterheads', svg: '/images/printing/letterheads.svg' },
        { name: 'ID Cards', svg: '/images/printing/id-card.webp' },
        { name: 'Appointment Cards', svg: '/images/printing/appointment-cards.webp' },
        { name: 'Greeting Cards', svg: '/images/printing/greeting-cards.webp' },
        { name: 'Gift Cards', svg: '/images/printing/gift-cards.webp' },
        { name: 'Membership Cards', svg: '/images/printing/membership-cards.webp' },
        { name: 'Invoice Books', svg: '/images/printing/invoice-books.webp' },
        { name: 'Receipt Books', svg: '/images/printing/receipt-books.webp' },
        { name: 'Bill Books', svg: '/images/printing/bill-books.webp' },
        { name: 'Writing Pads', svg: '/images/printing/writing-pads.webp' },
        { name: 'Office Diaries', svg: '/images/printing/office-diaries.webp' }
      ]
    },
    {
      title: 'Marketing & Promotional Prints',
      icon: Megaphone,
      badgeColor: 'from-cyan-500 to-blue-500',
      items: [
        { name: 'Flyers', svg: '/images/printing/flyers.webp' },
        { name: 'Leaflets', svg: '/images/printing/leaflets.webp' },
        { name: 'Pamphlets', svg: '/images/printing/pamphlets.webp' },
        { name: 'Brochures', svg: '/images/printing/brochures.webp' },
        { name: 'Booklets', svg: '/images/printing/booklets.webp' },
        { name: 'Catalogues', svg: '/images/printing/catalogues.webp' },
        { name: 'Product Catalogues', svg: '/images/printing/product-catalogues.webp' },
        { name: 'Presentation Folders', svg: '/images/printing/presentation-folders.webp' },
        { name: 'Certificates', svg: '/images/printing/certificates.webp' }
      ]
    },
    {
      title: 'Large Format Printing',
      icon: Maximize2,
      badgeColor: 'from-purple-500 to-pink-500',
      items: [
        { name: 'Posters', svg: '/images/printing/posters.webp' },
        { name: 'Folded Flyers', svg: '/images/printing/folded-flyers.webp' },
        { name: 'Flex Banners', svg: '/images/printing/flex-banners.webp' },
        { name: 'Vinyl Banners', svg: '/images/printing/vinyl-banners.webp' },
        { name: 'Roll-Up Standees', svg: '/images/printing/roll-up-standees.webp' },
        { name: 'Backdrop Printing', svg: '/images/printing/backdrop-printing.webp' },
        { name: 'Wall Graphics', svg: '/images/printing/wall-graphics.webp' },
        { name: 'Exhibition Stalls', svg: '/images/printing/exhibition-stalls.webp' },
        { name: 'Trade Show Displays', svg: '/images/printing/trade-show-displays.webp' },


      ]
    },
    {
      title: 'Books & Publications',
      icon: BookOpen,
      badgeColor: 'from-emerald-500 to-teal-500',
      items: [
        { name: 'Books', svg: '/images/printing/books.webp' },
        { name: 'Magazines', svg: '/images/printing/magazines.webp' },
        { name: 'Manuals', svg: '/images/printing/manuals.webp' },
        { name: 'Annual Reports', svg: '/images/printing/annual-reports.webp' },
        { name: 'Newsletters', svg: '/images/printing/newsletters.webp' },
        { name: 'Wall Calendars', svg: '/images/printing/wall-calendars.webp' },
        { name: 'Desk Calendars', svg: '/images/printing/desk-calendars.webp' },
        { name: 'Labels & Stickers', svg: '/images/printing/labels-stickers.webp' },
        { name: 'Invitation Cards', svg: '/images/printing/invitation-cards.webp' },
        { name: 'Wedding Cards', svg: '/images/printing/wedding-cards.webp' },

      ]
    }
  ];

  const capabilities = [
    'Premium Paper Stocks & Luxury Finishes',
    'Vibrant Color Accuracy & HD Print Resolution',
    'Fast Turnaround & Doorstep Express Delivery',
    'Custom Sizes, Embossing & Foil Stamping',
    'Bulk Discount Quotations for Corporate Orders',
    'Dedicated Printing Specialist Support'
  ];

  const handleCardClick = (item) => {
    setSelectedProduct(item);
    setIsSubmitted(false);
    setIsModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitRequest = (e) => {
    e.preventDefault();

    if (!formData.name || !formData.phone) {
      toast.error('Please enter your name and phone number');
      return;
    }

    const productName = selectedProduct ? selectedProduct.name : 'Print Solution';
    const messageText = `Hello HOASP International Print Team,%0A%0A*New Print Product Request Form*%0A- *Product:* ${productName}%0A- *Client Name:* ${formData.name}%0A- *Mobile:* ${formData.phone}%0A- *Email:* ${formData.email || 'N/A'}%0A- *Quantity:* ${formData.quantity}%0A- *Requirements:* ${formData.message || 'Call me back with instant price quotation.'}`;

    // 1. Mobile WhatsApp Trigger (Instant alert to company mobile & client)
    const whatsappUrl = `https://wa.me/918796118101?text=${messageText}`;

    // 2. Email Mailto Trigger (Sent to company inbox)
    const mailtoUrl = `mailto:info@hoasp.com?subject=Print Request: ${encodeURIComponent(productName)} - ${encodeURIComponent(formData.name)}&body=${decodeURIComponent(messageText).replace(/%0A/g, '%0D%0A')}`;

    // Open WhatsApp for instant mobile notification
    window.open(whatsappUrl, '_blank');

    // Trigger Email mailto link in background
    setTimeout(() => {
      window.location.href = mailtoUrl;
    }, 500);

    setIsSubmitted(true);
    toast.success(`Request submitted for ${productName}! Opening WhatsApp & Email notification.`);
  };

  return (
    <div className="min-h-screen bg-gray-950 pt-20 text-left">
      {/* Hero Section */}
      <section className="py-6 md:py-8 bg-gradient-to-b from-gray-900 via-slate-900 to-gray-950 border-b border-gray-800">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center">
            <div className="inline-block bg-orange-500/10 border border-orange-500/20 rounded-full px-3 py-1 mb-2">
              <span className="text-orange-400 text-xs font-semibold tracking-wide uppercase">Industrial & Enterprise Printing</span>
            </div>
            <h1 className="text-2xl md:text-4xl font-extrabold text-white mb-2 leading-tight">
              Next-Gen <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-500 bg-clip-text text-transparent">Printing & Web-to-Print Solutions</span>
            </h1>
            <p className="text-xs md:text-sm text-gray-300 mb-3.5 leading-relaxed max-w-xl mx-auto">
              Empower your print business with automated workflow management, interactive online product customizers, batch order processing, and real-time press tracking.
            </p>
            <div className="flex flex-col sm:flex-row gap-2.5 justify-center">
              <Button
                onClick={() => handleCardClick({ name: 'General Printing Inquiry', svg: '/images/printing/visiting-cards.svg' })}
                className="bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white px-5 py-2.5 text-xs md:text-sm rounded-xl font-bold shadow-lg shadow-orange-500/20"
              >
                Request Custom Quotation <ArrowRight className="ml-1.5 w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Clean Product Catalog Grid */}
      <section className="py-6 md:py-8 bg-gray-950 border-b border-gray-800/80">
        <div className="container mx-auto px-6">
          <div className="space-y-8">
            {printCategories.map((category, catIdx) => (
              <div key={catIdx} className="space-y-3">
                {/* Category Header */}
                <div className="flex items-center space-x-3 pb-1.5 border-b border-gray-800">
                  <div className={`w-8 h-8 bg-gradient-to-br ${category.badgeColor} rounded-lg flex items-center justify-center shadow-md`}>
                    <category.icon className="w-3.5 h-3.5 text-white" />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-white tracking-tight">{category.title}</h3>
                </div>

                {/* Grid of White Background Product Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-5">
                  {category.items.map((item, itemIdx) => (
                    <div
                      key={itemIdx}
                      onClick={() => handleCardClick(item)}
                      className="bg-white border border-gray-200 hover:border-orange-500 rounded-2xl overflow-hidden flex flex-col transition-all duration-300 hover:scale-[1.03] hover:shadow-xl group cursor-pointer"
                    >
                      {/* 1. FULL WIDTH TOP PRODUCT SVG IMAGE */}
                      <div className="w-full h-32 sm:h-36 bg-gray-100 relative overflow-hidden flex items-center justify-center">
                        <img
                          src={item.svg}
                          alt={item.name}
                          className="xw-full h-full px-2 py-2 object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>

                      {/* 2. SEPARATE BORDER LINE BELOW IMAGE */}
                      <div className="w-full border-b border-gray-200 group-hover:border-orange-500 transition-colors"></div>

                      {/* 3. PRODUCT TITLE HEADER (WHITE BACKGROUND) */}
                      <div className="p-3.5 flex-1 flex items-center justify-center text-center bg-white">
                        <span className="text-gray-900 group-hover:text-orange-600 text-xs md:text-sm font-bold tracking-wide transition-colors leading-snug">
                          {item.name}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Highlights & Capabilities */}
      <section className="py-20 bg-gray-900 border-t border-b border-gray-800">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-orange-400 text-sm font-semibold uppercase tracking-wider">High-Quality Printing</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mt-2 mb-6">
                Built for Businesses, Corporate Brands, & Personal Events
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-8">
                At HOASP International, we deliver top-tier print products with vibrant color precision, premium paper stocks, and rapid turnaround times. Whether you need corporate stationery, marketing collateral, or custom event invitations, we bring your vision to life.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {capabilities.map((cap, index) => (
                  <div key={index} className="flex items-center space-x-3 text-gray-300">
                    <CheckCircle2 className="w-5 h-5 text-orange-400 flex-shrink-0" />
                    <span className="text-sm font-medium">{cap}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-950 to-gray-900 border border-gray-800 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-orange-500/10 rounded-full blur-3xl"></div>
              <Printer className="w-16 h-16 text-orange-400 mb-6" />
              <h3 className="text-2xl font-bold text-white mb-4">Need Custom Printing or Bulk Orders?</h3>
              <p className="text-gray-400 mb-6 leading-relaxed">
                Have specialized design requirements, custom dimensions, or high-volume corporate orders? Contact our print experts for instant assistance, sample proofing, and wholesale pricing.
              </p>
              <Button
                onClick={() => handleCardClick({ name: 'Custom Bulk Printing Inquiry', svg: '/images/printing/visiting-cards.svg' })}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 rounded-xl shadow-lg"
              >
                Get Custom Print Quote
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* REQUEST FORM MODAL */}
      {isModalOpen && selectedProduct && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-white border border-gray-200 rounded-3xl max-w-lg w-full p-6 md:p-8 shadow-2xl relative overflow-hidden animate-in fade-in zoom-in duration-200 text-left">
            {/* Modal Close Button */}
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-5 right-5 text-gray-500 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 p-2 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Modal Header */}
            <div className="flex items-center space-x-4 mb-6 pb-4 border-b border-gray-200">
              <div className="w-16 h-12 bg-gray-50 border border-gray-200 rounded-xl overflow-hidden flex-shrink-0">
                <img src={selectedProduct.svg} alt={selectedProduct.name} className="w-full h-full object-cover" />
              </div>
              <div>
                <span className="text-orange-600 text-xs font-semibold uppercase tracking-wider flex items-center gap-1">
                  <PhoneCall className="w-3.5 h-3.5" /> Product Inquiry & Request Form
                </span>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900">{selectedProduct.name}</h3>
              </div>
            </div>

            {isSubmitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-emerald-100 border border-emerald-300 rounded-full flex items-center justify-center mx-auto mb-4 text-emerald-600">
                  <CheckCircle2 className="w-10 h-10" />
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-2">Request Transmitted!</h4>
                <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                  Your request for <strong className="text-orange-600">{selectedProduct.name}</strong> has been transmitted to our mobile team via WhatsApp and email.
                </p>
                <div className="p-4 bg-slate-50 border border-gray-200 rounded-2xl mb-6 text-xs text-gray-600 text-left space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span>Mobile Notification:</span>
                    <span className="text-emerald-600 font-semibold flex items-center gap-1"><Sparkles className="w-3 h-3" /> Sent to WhatsApp (+91 8796 118 101)</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Email Notification:</span>
                    <span className="text-blue-600 font-semibold">Sent to info@hoasp.com</span>
                  </div>
                </div>
                <Button
                  onClick={() => setIsModalOpen(false)}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3.5 rounded-xl"
                >
                  Close & Continue Browsing
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmitRequest} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Your Full Name *</label>
                  <div className="relative">
                    <User className="w-4 h-4 text-gray-400 absolute left-3.5 top-3.5" />
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="e.g. Rahul Sharma"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full bg-slate-50 border border-gray-300 rounded-xl pl-10 pr-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-500 focus:bg-white transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Mobile Phone Number *</label>
                    <div className="relative">
                      <Phone className="w-4 h-4 text-gray-400 absolute left-3.5 top-3.5" />
                      <input
                        type="tel"
                        name="phone"
                        required
                        placeholder="+91 98765 43210"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full bg-slate-50 border border-gray-300 rounded-xl pl-10 pr-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-500 focus:bg-white transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Email Address</label>
                    <div className="relative">
                      <Mail className="w-4 h-4 text-gray-400 absolute left-3.5 top-3.5" />
                      <input
                        type="email"
                        name="email"
                        placeholder="name@company.com"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full bg-slate-50 border border-gray-300 rounded-xl pl-10 pr-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-500 focus:bg-white transition-colors"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Estimated Quantity / Order Size</label>
                  <select
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleInputChange}
                    className="w-full bg-slate-50 border border-gray-300 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-orange-500 focus:bg-white transition-colors"
                  >
                    <option value="100 - 500 Pcs">100 - 500 Pcs</option>
                    <option value="500 - 1,000 Pcs">500 - 1,000 Pcs</option>
                    <option value="1,000 - 5,000 Pcs">1,000 - 5,000 Pcs</option>
                    <option value="5,000+ Bulk Commercial">5,000+ Bulk Commercial</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Special Requirements / Notes</label>
                  <textarea
                    name="message"
                    rows="3"
                    placeholder="Provide paper GSM, finishing, size, or custom design details..."
                    value={formData.message}
                    onChange={handleInputChange}
                    className="w-full bg-slate-50 border border-gray-300 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-500 focus:bg-white transition-colors"
                  ></textarea>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white font-bold py-4 rounded-xl shadow-lg shadow-orange-500/20 flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" /> Submit Request to Mobile & Email
                </Button>

                <p className="text-[11px] text-gray-500 text-center mt-2">
                  🔒 We respect your privacy. Request sent directly to mobile (+91 8796 118 101) & email.
                </p>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PrintingSolution;
